import React from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Avatar,
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import type { AccountUser, Account } from '@/types'

interface Props {
  member: AccountUser
}

export default function AccountUsersShow({ member }: Props) {
  const { currentAccount, isAccountAdmin, auth } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
    auth: { user: { id: number; email: string } }
  }>().props

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const isCurrentUser = member.user.id === auth.user.id

  const handleDelete = () => {
    router.delete(`/accounts/${currentAccount.slug}/members/${member.id}`)
  }

  return (
    <ManagementLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
            {member.user.email[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {member.user.email}
            </Typography>
            {isCurrentUser && (
              <Typography variant="body2" color="text.secondary">This is you</Typography>
            )}
          </Box>
        </Box>
        {isAccountAdmin && !isCurrentUser && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              href={`/accounts/${currentAccount.slug}/members/${member.id}/edit`}
              variant="outlined"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Remove
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Member Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body2">{member.user.email}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Role</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={member.role === 'admin' ? 'Admin' : 'Member'}
                    size="small"
                    color={member.role === 'admin' ? 'primary' : 'default'}
                  />
                </Box>
              </Grid>
              {member.createdAt && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Joined</Typography>
                  <Typography variant="body2">
                    {new Date(member.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Remove Team Member"
        message={`Are you sure you want to remove ${member.user.email} from this account? They will lose all access.`}
        confirmText="Remove"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </ManagementLayout>
  )
}
